import React from "react";
import { Avatar } from "../shared/Avatar";
import { CustomIcon } from "../shared/CustomIcon";
import { Card, Badge, StatusRing } from "../shared";
import { fullName, formatDate, formatMemberNumber } from "../../lib/format";

// ============================================================
// Phase 5B — Flock member row card
// ============================================================
// Semantic left border matches memberStatus:
//   Active → green · New Member → blue · Visitor → amber
//   Guest → purple · Inactive → red · Left Church / Deceased → grey
// Avatar is wrapped in StatusRing (ring only renders for privileged
// viewers). Empty fields render nothing — no `—` placeholders.
// Row height is ~30% taller than the previous table rows for better
// mobile tap targets.
// ============================================================
export const STATUS_VARIANT = {
  "Active":      "green",
  "New Member":  "blue",
  "Visitor":     "amber",
  "Guest":       "purple",
  "Inactive":    "red",
  "Left Church": "grey",
  "Deceased":    "grey",
};

export function FlockMemberCard({ member: m, viewerRole, onOpen, selectMode = false, selected = false, onToggleSelect }) {
  const variant = STATUS_VARIANT[m.memberStatus] || "none";
  const badgeVariant = variant === "none" ? "grey" : variant;
  const contact = !m.masked
    ? ((Array.isArray(m.phone) && m.phone[0]?.number) || (Array.isArray(m.email) && m.email[0]?.address) || "")
    : "";
  const dept = !m.masked ? ((m.departments || [])[0] || "") : "";
  const joined = !m.masked && m.joinedDate ? formatDate(m.joinedDate) : "";

  return (
    <Card
      variant={variant}
      data-testid={`member-row-${m.id}`}
      onClick={onOpen}
      style={{
        cursor: "pointer",
        padding: "20px 18px",
        display: "flex",
        alignItems: "center",
        gap: 14,
        background: selectMode && selected ? "var(--kc-accent-bg)" : undefined,
      }}
    >
      {selectMode && (
        <input
          type="checkbox"
          data-testid={`member-select-${m.id}`}
          checked={selected}
          onChange={(e) => { e.stopPropagation(); onToggleSelect && onToggleSelect(); }}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${fullName(m)}`}
          style={{ width: 20, height: 20, accentColor: "var(--kc-accent)", flexShrink: 0 }}
        />
      )}
      <StatusRing status={m.memberStatus} viewerRole={viewerRole} style={{ flexShrink: 0 }}>
        <Avatar name={fullName(m)} photo={m.photo} status={m.memberStatus} noRing style={{ fontSize: 12 }} />
      </StatusRing>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span className="text-card-primary" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{fullName(m)}</span>
          {m.masked && (
            <Badge
              variant="grey"
              data-testid={`member-masked-badge-${m.id}`}
              title="Profile details restricted — not assigned to you"
            >
              <CustomIcon name="lock" size={10} style={{ marginRight: 4 }} /> Restricted
            </Badge>
          )}
        </div>
        {!m.masked && m.memberNumber ? (
          <div
            className="text-card-secondary"
            data-testid={`member-row-number-${m.id}`}
          >
            {formatMemberNumber(m.memberNumber)}
          </div>
        ) : null}
        {contact ? (
          <div className="text-card-secondary" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {contact}
          </div>
        ) : null}
        {(dept || joined) && (
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 2 }}>
            {dept ? <span className="text-meta">{dept}</span> : null}
            {joined ? <span className="text-meta">Joined {joined}</span> : null}
          </div>
        )}
      </div>
      {!m.masked && m.memberStatus ? (
        <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
          <Badge variant={badgeVariant}>{m.memberStatus}</Badge>
        </div>
      ) : null}
    </Card>
  );
}
